import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeftCircle, ArrowRightCircle, RotateCcw, Save } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { saveReflection } from "@/lib/smart";
import { isStudent } from "@/lib/classroom_live";

const PROP_POS = [["Front-left", "आगे-बाएँ"], ["Front-right", "आगे-दाएँ"], ["Rear-right", "पीछे-दाएँ"], ["Rear-left", "पीछे-बाएँ"]];

export const Check = ({ check, wrong, onAnswer }) => (
  <div className="rounded-2xl border-2 border-[#FFC94D] bg-[#FFF8E6] p-4" data-testid="lab-check">
    <div className="font-extrabold text-[#0A192F]" data-testid="lab-check-q">{check.q}</div>
    <div className="mt-3 space-y-2">
      {check.options.map((o, k) => (
        <button key={k} onClick={() => onAnswer(k)} data-testid={`lab-check-option-${k}`}
          className={`w-full text-left px-3 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${wrong === k ? "border-[#FF3B3B] bg-[#FF3B3B]/10" : "bg-white border-slate-300 hover:border-[#00B8D4]"}`}>{o}</button>
      ))}
    </div>
  </div>
);

export const PropsConfig = ({ props, setProps, onDone }) => {
  const { L } = useLang();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="lab-props">
      <div className="text-sm font-bold text-[#0A192F]">{L("Tap each propeller to set its spin", "हर propeller पर tap करके घुमाव चुनिए")}</div>
      <div className="mt-1 text-[11px] text-slate-500"><span className="text-[#007A8A] font-bold">■ CW</span> {L("clockwise", "क्लॉकवाइज़")} · <span className="text-[#C81E4B] font-bold">■ CCW</span> {L("anticlockwise", "एंटी-क्लॉकवाइज़")} · {L("front = away from you", "आगे = आपसे दूर")}</div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {[0, 1, 3, 2].map((i) => (
          <button key={i} onClick={() => setProps(props.map((x, k) => (k === i ? (x === "cw" ? "ccw" : "cw") : x)))} data-testid={`lab-prop-${i}`}
            className={`h-14 rounded-xl border-2 font-bold text-xs transition-colors ${props[i] === "cw" ? "border-[#00B8D4] bg-[#E0FAFF] text-[#00707F]" : "border-[#FF2E63] bg-[#FFE4EC] text-[#A3123E]"}`}>
            {L(...PROP_POS[i])}<br />{props[i] === "cw" ? "↻ CW" : "↺ CCW"}
          </button>
        ))}
      </div>
      {onDone && <button onClick={onDone} data-testid="lab-props-done-btn" className="mt-3 w-full h-11 rounded-full bg-[#0A192F] text-white font-bold">{L("Directions set", "दिशा सेट हो गई")}</button>}
    </div>
  );
};

export const RuleConfig = ({ q, rule, setRule, onDone }) => {
  const { L } = useLang();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="lab-rule">
      <div className="text-sm font-bold text-[#0A192F]" data-testid="lab-rule-q">{q}</div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {[["left", ArrowLeftCircle, "Turn LEFT", "बाएँ मुड़े"], ["right", ArrowRightCircle, "Turn RIGHT", "दाएँ मुड़े"]].map(([k, Icon, en, h]) => (
          <button key={k} onClick={() => setRule(k)} data-testid={`lab-rule-${k}`} aria-pressed={rule === k}
            className={`h-14 rounded-xl border-2 font-bold text-sm inline-flex items-center justify-center gap-2 transition-colors ${rule === k ? "border-[#00B8D4] bg-[#E0FAFF] text-[#00707F]" : "border-slate-200 hover:border-[#00B8D4]"}`}>
            <Icon className="w-5 h-5" />{L(en, h)}</button>
        ))}
      </div>
      {onDone && <button onClick={onDone} disabled={!rule} data-testid="lab-rule-done-btn" className="mt-3 w-full h-11 rounded-full bg-[#0A192F] text-white font-bold disabled:opacity-40">{L("Rule set", "नियम सेट")}</button>}
    </div>
  );
};

export const Reflection = ({ labId, onRestart }) => {
  const { L } = useLang();
  const [text, setText] = useState("");
  const [saved, setSaved] = useState(false);
  const save = async () => {
    try { await saveReflection(labId, text.trim()); setSaved(true); toast.success(L("Saved to your portfolio", "आपके पोर्टफ़ोलियो में सेव हो गया")); }
    catch { toast.error(L("Could not save. Try again.", "सेव नहीं हुआ। फिर कोशिश करें।")); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="lab-reflection">
      <div className="text-sm font-bold text-[#0A192F]">{L("What did you learn? (goes into your portfolio)", "आपने क्या सीखा? (पोर्टफ़ोलियो में जाएगा)")}</div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={400} rows={3} disabled={saved} data-testid="lab-reflection-input"
        className="mt-2 w-full rounded-xl border border-slate-300 bg-[#F0F4F8] p-3 text-sm outline-none focus:border-[#00B8D4]" placeholder={L("e.g. The motor driver gives the motors enough current…", "जैसे: motor driver motors को पूरा current देता है…")} />
      {!isStudent() && <p className="text-[11px] text-slate-500">{L("Saved on this device. Log in as a student to keep it in your RoboSpark portfolio.", "इस डिवाइस पर सेव होगा। RoboSpark पोर्टफ़ोलियो के लिए छात्र लॉगिन करें।")}</p>}
      {saved && <p className="text-[11px] font-semibold text-emerald-700" data-testid="lab-reflection-saved">{L("Saved to your portfolio.", "पोर्टफ़ोलियो में सेव हो गया।")}</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        <button onClick={save} disabled={saved || text.trim().length < 2} data-testid="lab-reflection-save" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#FF3B3B] text-white text-sm font-bold disabled:opacity-40"><Save className="w-4 h-4" />{saved ? L("Saved", "सेव हुआ") : L("Save", "सेव करें")}</button>
        <button onClick={onRestart} data-testid="lab-restart-btn" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-slate-300 text-sm font-bold"><RotateCcw className="w-4 h-4" />{L("Build again", "फिर से बनाएँ")}</button>
      </div>
    </div>
  );
};
